import type { ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import {
  subscriptionsApi,
  type OrgSubscriptionPermissions,
} from 'src/api/subscriptionsApi';
import useAuth from 'src/hooks/useAuth';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import logger from 'src/utils/logger';
import { expandRolePermissionKeys } from 'src/mindtime/utils/permissionKeys';

const EMPTY_SUBSCRIPTION_KEYS: ReadonlySet<string> = new Set();
const EMPTY_SUBSCRIBED_SOLUTIONS: ReadonlySet<string> = new Set();

type SubscriptionPermissionsContextValue = {
  resolved: boolean;
  /** Null when entitlements failed to load; otherwise parsed org subscription state. */
  orgEntitlements: OrgSubscriptionPermissions | null;
  subscriptionKeys: ReadonlySet<string>;
  /** JWT/role permission identifiers for the logged-in user (client and reseller). */
  roleKeys: ReadonlySet<string>;
  /** Solution slugs from BE (`solution` on each product row, e.g. `edr`). */
  subscribedSolutions: ReadonlySet<string>;
  hasActiveSubscription: boolean;
};

const SubscriptionPermissionsContext = createContext<SubscriptionPermissionsContextValue>({
  resolved: false,
  orgEntitlements: null,
  subscriptionKeys: EMPTY_SUBSCRIPTION_KEYS,
  roleKeys: EMPTY_SUBSCRIPTION_KEYS,
  subscribedSolutions: EMPTY_SUBSCRIBED_SOLUTIONS,
  hasActiveSubscription: false,
});

type SubscriptionPermissionsProviderProps = {
  children: ReactNode;
};

export const SubscriptionPermissionsProvider = ({
  children,
}: SubscriptionPermissionsProviderProps) => {
  const { user, isAuthenticated } = useAuth();
  const { pathname } = useLocation();
  const { userData } = useUserProfile();
  const orgId = userData?.organization?.id || user?.organizationId || '';
  const profileReady = userData !== null;
  const lastOrgIdRef = useRef('');

  const [resolved, setResolved] = useState(false);
  const [orgEntitlements, setOrgEntitlements] = useState<OrgSubscriptionPermissions | null>(null);

  useEffect(() => {
    if (!orgId) {
      if (isAuthenticated && !user?.organizationId && !profileReady) {
        setResolved(false);
        return undefined;
      }

      lastOrgIdRef.current = '';
      setOrgEntitlements(emptyOrgEntitlements());
      setResolved(true);
      return undefined;
    }

    const orgChanged = lastOrgIdRef.current !== orgId;
    lastOrgIdRef.current = orgId;

    let cancelled = false;

    if (orgChanged) {
      setOrgEntitlements(null);
      setResolved(false);
    }

    const load = async (): Promise<void> => {
      try {
        const next = await subscriptionsApi.getOrgPermissions(orgId);
        if (!cancelled) {
          setOrgEntitlements(next);
        }
      } catch (error) {
        if (!cancelled) {
          logger(error, 'error', 'SubscriptionPermissionsProvider');
          setOrgEntitlements(null);
        }
      } finally {
        if (!cancelled) {
          setResolved(true);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [orgId, pathname, isAuthenticated, user?.organizationId, profileReady]);

  const subscriptionKeys = useMemo(
    () => new Set(orgEntitlements?.subscriptionKeys ?? []),
    [orgEntitlements?.subscriptionKeys],
  );

  const roleKeys = useMemo(
    () => expandRolePermissionKeys(user?.permissions),
    [user?.permissions],
  );

  const subscribedSolutions = useMemo(
    () => new Set(orgEntitlements?.subscribedSolutions ?? []),
    [orgEntitlements?.subscribedSolutions],
  );

  const hasActiveSubscription = Boolean(orgEntitlements?.hasSubscriptions);

  const value = useMemo(
    () => ({
      resolved,
      orgEntitlements,
      subscriptionKeys,
      roleKeys,
      subscribedSolutions,
      hasActiveSubscription,
    }),
    [
      resolved,
      orgEntitlements,
      subscriptionKeys,
      roleKeys,
      subscribedSolutions,
      hasActiveSubscription,
    ],
  );

  return (
    <SubscriptionPermissionsContext.Provider value={value}>
      {children}
    </SubscriptionPermissionsContext.Provider>
  );
};

const emptyOrgEntitlements = (): OrgSubscriptionPermissions => ({
  organizationId: '',
  hasSubscriptions: false,
  subscribedSolutions: [],
  subscribedDomains: [],
  subscribedCategories: [],
  subscriptionKeys: [],
});

export const useSubscriptionPermissionsContext = (): SubscriptionPermissionsContextValue =>
  useContext(SubscriptionPermissionsContext);
